import React, { FC, useState } from 'react';
import { observer } from 'mobx-react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';
import { Elements } from '@stripe/react-stripe-js';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTheme } from '@material-ui/core/styles';

import { PartnerFormProps } from '@containers/checkout/payment-form/types';
import PayButton from '@components/PayButton';
import useStore from '@hooks/useStore';
import { FieldValues } from 'react-hook-form/dist/types';
import { Partner } from '@constants/paymentLink';
import FormPartnerLogo from '@components/FormPartnerLogo';
import PayerFields from './PayerFields';
import useStyles from './styles';

const StripeCardFormContent: FC<PartnerFormProps> = ({ onValidationError }) => {
  const css = useStyles();
  const { handleSubmit } = useFormContext();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasCardNumber, setHasCardNumber] = useState(false);
  const [hasCardExpiry, setHasCardExpiry] = useState(false);
  const [hasCardCvc, setHasCardCvc] = useState(false);
  const [cardBrand, setCardBrand] = useState<string>('');
  const {
    stripe: stripeStore,
    links: {
      paymentLink: { paymentLinkId, isSubscription },
    },
    checkout,
  } = useStore();
  const theme = useTheme();

  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '48px',
        color: '#4C545B',
        fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
        backgroundColor: 'transparent',
        '::placeholder': {
          color: 'transparent',
        },
        ':focus': {
          color: '#4C545B',
        },
        ':-webkit-autofill': {
          color: '#4C545B',
        },
      },
      invalid: {
        color: theme.oysterTheme.ferrari,
        ':focus': {
          color: theme.oysterTheme.ferrari,
        },
      },
    },
  };

  const EXPIRY_ELEMENT_OPTIONS = {
    style: {
      base: {
        ...ELEMENT_OPTIONS.style.base,
        '::placeholder': {
          color: 'transparent',
          opacity: '0',
          transition: 'color 0.3s ease, opacity 0.3s ease',
          transitionDelay: '0.3s',
        },
        ':focus': {
          ...ELEMENT_OPTIONS.style.base[':focus'],
          '::placeholder': {
            color: '#8F9499',
            opacity: '1',
            transition: 'color 0.3s ease, opacity 0.3s ease',
            transitionDelay: '0.3s',
          },
        },
      },
      invalid: ELEMENT_OPTIONS.style.invalid,
    },
    placeholder: 'MM/YY',
  };

  const CVC_ELEMENT_OPTIONS = {
    style: {
      base: {
        ...ELEMENT_OPTIONS.style.base,
        type: 'password',
      },
      invalid: ELEMENT_OPTIONS.style.invalid,
    },
  };

  const onSubmit = async (data: any) => {
    stripeStore.setErrorMessage('');
    if (!stripe || !elements) {
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      return;
    }
    setIsProcessing(true);

    try {
      const { token, error } = await stripe.createToken(cardNumber);
      if (error) {
        const errorMessage =
          typeof error.message === 'string' ? error.message : 'Error processing card';
        stripeStore.setErrorMessage(errorMessage);
        return;
      }

      const { success, metadata } = await stripeStore.pay({
        cardNumber: token.id,
        cardHolderName: data.cardHolderName,
        paymentLinkId,
        payeeFirstName: data.payeeFirstName,
        payeeLastName: data.payeeLastName,
        payeeEmail: data.payeeEmail,
        payeePhone: data.payeePhone,
        bin: '', // Por seguridad, Stripe no nos permite acceder al BIN
        last4: token.card?.last4 || '',
      });

      // Handle 3DS if required
      if (metadata?.redirectUrl) {
        const response = await stripe.confirmCardPayment(metadata.redirectUrl);
        console.log(response);
        const { error: confirmError } = response;

        if (response.paymentIntent?.status === 'succeeded') {
          const { id: transactionId } = response.paymentIntent;
          await stripeStore.confirm3dsPaymentStatus({
            transactionId,
            partner: Partner.STRIPE,
          });
          checkout.setCardPaymentSuccess();
          setIsProcessing(false);
          return;
        }

        if (confirmError) {
          const confirmErrorMessage =
            typeof confirmError.message === 'string'
              ? confirmError.message
              : 'Error confirming payment';
          stripeStore.setErrorMessage(confirmErrorMessage);
          setIsProcessing(false);
          return;
        }
      } else {
        if (success) {
          checkout.setCardPaymentSuccess();
        }
      }
    } catch (e: any) {
      setIsProcessing(false);
      stripeStore.setErrorMessage(e?.msg || e?.message || 'Error al procesar el pago');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {stripeStore.errorMessage !== '' && (
        <Grid item className={css.paymentError}>
          <ErrorIcon /> {stripeStore.errorMessage}
        </Grid>
      )}
      <PayerFields errorFields={stripeStore.errorMessage !== ''} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div
            className={`${css.cardElementContainer} ${hasCardNumber ? 'has-content' : ''} ${
              cardBrand ? `brand-${cardBrand}` : ''
            } ${stripeStore.errorMessage ? 'has-error' : ''} ${
              isProcessing ? 'is-processing' : ''
            }`}
          >
            <CardNumberElement
              options={ELEMENT_OPTIONS}
              onChange={(e) => {
                setHasCardNumber(!e.empty);
                setCardBrand(e.brand);
              }}
            />
            <span className={css.inputLabel}>
              {t('checkoutScreen.payment.methods.card.number')}
            </span>
          </div>
        </Grid>
        <Grid item xs={6}>
          <div
            className={`${css.cardElementContainer} ${hasCardExpiry ? 'has-content' : ''} ${
              stripeStore.errorMessage ? 'has-error' : ''
            } ${isProcessing ? 'is-processing' : ''}`}
          >
            <CardExpiryElement
              options={EXPIRY_ELEMENT_OPTIONS}
              onChange={(e) => setHasCardExpiry(!e.empty)}
            />
            <span className={css.inputLabel}>
              {t('checkoutScreen.payment.methods.card.validity')}
            </span>
          </div>
        </Grid>
        <Grid item xs={6}>
          <div
            className={`${css.cardElementContainer} ${hasCardCvc ? 'has-content' : ''} ${
              stripeStore.errorMessage ? 'has-error' : ''
            } ${isProcessing ? 'is-processing' : ''}`}
          >
            <CardCvcElement
              options={CVC_ELEMENT_OPTIONS}
              onChange={(e) => setHasCardCvc(!e.empty)}
            />
            <span className={css.inputLabel}>{t('checkoutScreen.payment.methods.card.cvv')}</span>
          </div>
        </Grid>
      </Grid>
      <PayButton
        onClick={handleSubmit(onSubmit as SubmitHandler<FieldValues>, onValidationError)}
        disabled={!stripe || !elements || isProcessing || !checkout.isAllowedToPayWithCard}
        loading={isProcessing}
        isSubscription={isSubscription}
        label={
          !isSubscription
            ? t('checkoutScreen.payment.methods.submit')
            : t('checkoutScreen.payment.methods.subscribe')
        }
      />
      <FormPartnerLogo partner={Partner.STRIPE} />
    </>
  );
};

const StripeCardForm: FC<PartnerFormProps> = (props) => {
  const {
    links: {
      paymentConfig: {
        paymentMethods: { card },
      },
    },
  } = useStore();

  if (
    card.connectedAccountId !== '' &&
    card.connectedAccountId !== null &&
    card.connectedAccountId !== undefined
  ) {
    return (
      <Elements stripe={loadStripe(card.publicApiKey, { stripeAccount: card.connectedAccountId })}>
        <StripeCardFormContent {...props} />
      </Elements>
    );
  }

  return (
    <Elements stripe={loadStripe(card.publicApiKey)}>
      <StripeCardFormContent {...props} />
    </Elements>
  );
};

export default observer(StripeCardForm);
